// Settings card (office and admin, Session AUE, Director decisions D-LATE-1 and D-LATE-2): the late-reply opener.
//
// A reply that goes out on a later working day than the message arrived carries one fixed line straight after its greeting,
// added by the platform (never written by the assistant, and never giving a reason for the delay): the late line from one
// working day, the very late line from twenty. The office may change either line; "Use the default" puts both back. Every
// change is previewed first (how a reply opens with each line, and any check it fails), then saved through a two-step
// confirm. Audited server side. A very late fault or complaint gets no draft at all: the office phones them.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { LateReplyWordingChange, LateReplyWordingSettings } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { formatDateTime } from "../../lib/format";
import { Loader } from "../../components/Loader";

type Pending = { change: LateReplyWordingChange; late: string; veryLate: string; ok: boolean; refusals: string[]; lateSample: string; veryLateSample: string; isDefault: boolean };

const preCls = "whitespace-pre-wrap break-words rounded-ele border border-ele-hairline bg-ele-inset p-3 font-body text-sm text-ele-strong";
const linkBtn = "font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const fieldCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 font-body text-sm text-ele-strong outline-none focus:border-ele-green";

function Samples({ late, veryLate, testId }: { late: string; veryLate: string; testId: string }) {
  return (
    <div className="grid gap-3 lg:grid-cols-2" data-testid={testId}>
      <div>
        <p className={labelCls}>A late reply opens</p>
        <pre className={preCls} data-testid={`${testId}-late`}>
          {late}
        </pre>
      </div>
      <div>
        <p className={labelCls}>A very late reply opens</p>
        <pre className={preCls} data-testid={`${testId}-very-late`}>
          {veryLate}
        </pre>
      </div>
    </div>
  );
}

export function LateReplyWordingSettingsCard({ refreshKey }: { refreshKey?: number }) {
  const [view, setView] = useState<LateReplyWordingSettings | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [editing, setEditing] = useState(false);
  const [late, setLate] = useState("");
  const [veryLate, setVeryLate] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState<"preview" | "save" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setState("loading");
    Promise.resolve()
      .then(() => api.settings.lateReplyWording.get())
      .then((v) => {
        if (!live) return;
        setView(v);
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  async function preview(change: LateReplyWordingChange) {
    setBusy("preview");
    setError(null);
    setMessage(null);
    setPending(null);
    try {
      const res = await api.settings.lateReplyWording.put({ ...change, preview: true });
      if (res.preview) {
        setPending({ change, late: res.late, veryLate: res.veryLate, ok: res.ok, refusals: res.refusals, lateSample: res.lateSample, veryLateSample: res.veryLateSample, isDefault: res.isDefault });
      }
    } catch (e) {
      setError(friendlyApiError(e, "Could not check the wording. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!pending || !pending.ok) return;
    setBusy("save");
    setError(null);
    try {
      const res = await api.settings.lateReplyWording.put({ ...pending.change, preview: false });
      if (!res.preview) {
        setView(res);
        setMessage(pending.change.useDefault ? "The default wording is in use again." : "Saved. Every late reply from now on opens with it.");
        setPending(null);
        setEditing(false);
      }
    } catch (e) {
      setError(friendlyApiError(e, "Could not save the wording. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  function startEditing() {
    if (!view) return;
    setLate(view.late);
    setVeryLate(view.veryLate);
    setPending(null);
    setError(null);
    setMessage(null);
    setEditing(true);
  }

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Late reply wording">
      <h2 className="mb-2 font-heading text-lg text-ele-blue">Late reply wording</h2>
      <p className="mb-3 text-sm text-ele-body">
        When a reply goes out on a later working day than the email arrived, the platform adds one short apology straight after the greeting. The
        assistant never writes its own, and the line never gives a reason. A fault or complaint that has waited 20 or more working days gets no draft
        at all: it is put in front of you to phone them.
      </p>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !view ? (
        <p role="alert" className="text-sm text-ele-strong">
          Could not load the wording.
        </p>
      ) : (
        <div className="space-y-3">
          {!pending && <Samples late={view.lateSample} veryLate={view.veryLateSample} testId="late-wording-current" />}
          <p className="text-xs text-ele-body">
            Late means {view.lateAtWorkingDays} or more working days; very late means {view.veryLateAtWorkingDays} or more.{" "}
            {view.isDefault ? "This is the default wording." : "This is the office's own wording."}
            {view.updatedAt ? ` Last changed ${formatDateTime(view.updatedAt)}${view.updatedByName ? ` by ${view.updatedByName}` : ""}.` : ""}
          </p>

          {!editing ? (
            <button type="button" onClick={startEditing} className={linkBtn}>
              Change the wording
            </button>
          ) : (
            <div className="space-y-3">
              <label className="block">
                <span className={labelCls}>Late line ({view.lateAtWorkingDays} or more working days)</span>
                <input
                  aria-label="Late line"
                  value={late}
                  maxLength={view.maxLength}
                  onChange={(e) => {
                    setLate(e.target.value);
                    setPending(null);
                  }}
                  className={fieldCls}
                />
              </label>
              <label className="block">
                <span className={labelCls}>Very late line ({view.veryLateAtWorkingDays} or more working days)</span>
                <textarea
                  aria-label="Very late line"
                  value={veryLate}
                  maxLength={view.maxLength}
                  rows={2}
                  onChange={(e) => {
                    setVeryLate(e.target.value);
                    setPending(null);
                  }}
                  className={fieldCls}
                />
              </label>
              <p className="text-xs text-ele-body">One short paragraph each, saying sorry for the slow reply. No reason for the delay, no name and no slang.</p>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => void preview({ late, veryLate, preview: true })}
                  disabled={busy != null || !late.trim() || !veryLate.trim()}
                  className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-strong transition hover:text-ele-green disabled:opacity-50"
                >
                  {busy === "preview" ? "Checking..." : "Preview"}
                </button>
                {!view.isDefault && (
                  <button type="button" onClick={() => void preview({ useDefault: true, preview: true })} disabled={busy != null} className={linkBtn}>
                    Use the default
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setPending(null);
                    setError(null);
                  }}
                  disabled={busy === "save"}
                  className={linkBtn}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {pending && (
            <div className="rounded-ele border border-ele-hairline p-3" data-testid="late-wording-preview">
              <p className="mb-2 font-menu text-xs uppercase tracking-wide text-ele-body">{pending.isDefault ? "The default wording" : "The new wording"}</p>
              <Samples late={pending.lateSample} veryLate={pending.veryLateSample} testId="late-wording-new" />
              {pending.refusals.length > 0 ? (
                <div className="mt-2" role="alert">
                  <p className="text-sm font-semibold text-ele-danger">It cannot be saved yet:</p>
                  <ul className="mt-1 space-y-1 text-sm">
                    {pending.refusals.map((r) => (
                      <li key={r} className="text-ele-body">
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="mt-3">
                  <ConfirmButton
                    onConfirm={() => void save()}
                    label={pending.change.useDefault ? "Use the default wording" : "Save this wording"}
                    confirmLabel="Every late reply will open with it. Press again"
                    busy={busy === "save"}
                    busyLabel="Saving..."
                    disabled={busy != null}
                    className="rounded-ele border border-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-green"
                    armedClassName="!text-ele-green font-semibold"
                  />
                </div>
              )}
            </div>
          )}
          {error && (
            <p role="alert" className="text-sm text-ele-danger">
              {error}
            </p>
          )}
          {message && (
            <p role="status" className="text-sm text-ele-strong">
              {message}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
